"use strict";
/**
 * Experiment Run Model
 *
 * This module defines the experiment run model for A/B testing and feature experiments.
 * Experiments are first-class citizens with explicit lifecycle management.
 *
 * Key principles:
 * - Manual resets only (preserves data interpretability)
 * - Deficit-based allocation for new exposures
 * - Stratified allocation for dependent experiments
 * - Feasibility reporting when targets are unachievable
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserAssignmentsSchema = exports.ExperimentAssignmentSchema = exports.ExperimentRunSchema = exports.FeasibilityStatusSchema = exports.StratificationConfigSchema = exports.VariantWeightsSchema = exports.AllocationType = exports.ExperimentStatus = void 0;
exports.computeVariantDeficits = computeVariantDeficits;
exports.selectVariantByDeficit = selectVariantByDeficit;
exports.computeExperimentBucket = computeExperimentBucket;
exports.selectVariantByBucket = selectVariantByBucket;
exports.calculateFeasibility = calculateFeasibility;
exports.assignVariant = assignVariant;
exports.createExperimentRun = createExperimentRun;
const zod_1 = require("zod");
const rules_1 = require("./rules");
/**
 * Experiment status lifecycle
 */
exports.ExperimentStatus = zod_1.z.enum(["draft", "running", "paused", "concluded"]);
/**
 * Allocation type for experiment assignment
 */
exports.AllocationType = zod_1.z.enum([
    /**
     * Deterministic hashing - stable assignments based on hash(uid, salt)
     * Good for: Simple rollouts, non-sticky experiments
     */
    "deterministic",
    /**
     * Deficit-based allocation - steers toward target balance using new exposures
     * Good for: Experiments where you want to maintain balance as you add variants
     */
    "deficit",
]);
/**
 * Variant weights - maps variant ID to weight (0-100)
 */
exports.VariantWeightsSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.number().min(0).max(100));
/**
 * Stratification configuration for dependent experiments
 *
 * When an experiment depends on a parent experiment, the allocation
 * can be stratified by the parent's variant to ensure proper balance.
 */
exports.StratificationConfigSchema = zod_1.z.object({
    /**
     * The parent experiment key
     */
    parentExperimentKey: zod_1.z.string(),
    /**
     * Per-stratum (parent variant) configuration
     * Maps parent variant -> { variants, weights }
     */
    byVariant: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        /**
         * Variants available in this stratum
         */
        variants: zod_1.z.array(zod_1.z.string()),
        /**
         * Weights for variants in this stratum
         */
        weights: exports.VariantWeightsSchema,
    })),
});
/**
 * Feasibility status for an experiment
 *
 * Reports whether the target distribution is achievable given current assignments.
 */
exports.FeasibilityStatusSchema = zod_1.z.object({
    /**
     * Whether the target distribution is achievable
     */
    achievable: zod_1.z.boolean(),
    /**
     * Minimum additional exposures needed to approach target balance
     * Only set if achievable is true but balance hasn't been reached
     */
    minAdditionalExposures: zod_1.z.number().optional(),
    /**
     * Reasons why the target is not achievable
     * e.g., "Variant X has 0% target but 100 assignments"
     */
    blockers: zod_1.z.array(zod_1.z.string()).optional(),
});
/**
 * Experiment run - a single "run" of an experiment
 *
 * Runs are the unit of stickiness - assignments are keyed by runId.
 * Starting a new run resets all assignments.
 */
exports.ExperimentRunSchema = zod_1.z.object({
    /**
     * Unique identifier for this run
     * Only changes on manual reset (starting a new run)
     */
    runId: zod_1.z.string(),
    /**
     * The experiment key this run belongs to
     */
    experimentKey: zod_1.z.string(),
    /**
     * Environment this run is for
     */
    environment: zod_1.z.string(),
    /**
     * Available variants in this experiment
     */
    variants: zod_1.z.array(zod_1.z.string()),
    /**
     * Target weights for each variant (should sum to 100)
     * Can be changed mid-run without resetting assignments
     */
    targetWeights: exports.VariantWeightsSchema,
    /**
     * Eligibility condition - who can be assigned to this experiment
     */
    eligibility: rules_1.ConditionSchema,
    /**
     * SDK event name that triggers assignment
     * e.g., "viewed_feature", "clicked_button"
     */
    exposureTrigger: zod_1.z.string(),
    /**
     * How to allocate new users to variants
     */
    allocationType: exports.AllocationType,
    /**
     * Stratification config for dependent experiments
     */
    stratification: exports.StratificationConfigSchema.optional(),
    /**
     * Observed counts per variant (updated on each exposure)
     */
    observedCounts: zod_1.z.record(zod_1.z.string(), zod_1.z.number()).default({}),
    /**
     * Total number of users assigned
     */
    totalAssigned: zod_1.z.number().default(0),
    /**
     * Computed feasibility status
     */
    feasibility: exports.FeasibilityStatusSchema.optional(),
    /**
     * Current status
     */
    status: exports.ExperimentStatus,
    /**
     * When this run was created
     */
    createdAt: zod_1.z.number(),
    /**
     * When this run was last updated
     */
    updatedAt: zod_1.z.number(),
    /**
     * Who created this run
     */
    createdBy: zod_1.z.string().optional(),
});
/**
 * User assignment for an experiment
 */
exports.ExperimentAssignmentSchema = zod_1.z.object({
    /**
     * The run ID this assignment belongs to
     */
    runId: zod_1.z.string(),
    /**
     * The assigned variant
     */
    variant: zod_1.z.string(),
    /**
     * When the assignment was made
     */
    assignedAt: zod_1.z.number(),
    /**
     * What event triggered the assignment
     */
    exposureEvent: zod_1.z.string(),
    /**
     * Parent variant if this is a stratified assignment
     */
    stratumId: zod_1.z.string().optional(),
});
/**
 * User assignments document - all experiment assignments for a user
 */
exports.UserAssignmentsSchema = zod_1.z.record(zod_1.z.string(), exports.ExperimentAssignmentSchema);
/**
 * Compute the deficit for each variant
 *
 * deficit_i = targetWeight_i * (totalAssigned + 1) - observedCount_i
 *
 * The variant with the highest deficit should be assigned next.
 */
function computeVariantDeficits(targetWeights, observedCounts, totalAssigned) {
    const deficits = {};
    const nextTotal = totalAssigned + 1;
    for (const [variant, weight] of Object.entries(targetWeights)) {
        const targetCount = (weight / 100) * nextTotal;
        const currentCount = observedCounts[variant] ?? 0;
        deficits[variant] = targetCount - currentCount;
    }
    return deficits;
}
/**
 * Select the variant with the highest deficit
 *
 * When multiple variants have equal deficits (e.g., at initialization when all counts are 0),
 * ties are broken alphabetically by variant name to ensure deterministic behavior.
 */
function selectVariantByDeficit(targetWeights, observedCounts, totalAssigned) {
    // Validate that we have at least one variant
    const variantKeys = Object.keys(targetWeights);
    if (variantKeys.length === 0) {
        throw new Error("Cannot select variant: no variants defined in targetWeights");
    }
    const deficits = computeVariantDeficits(targetWeights, observedCounts, totalAssigned);
    let maxDeficit = -Infinity;
    let selectedVariant = "";
    for (const [variant, deficit] of Object.entries(deficits)) {
        // Deterministic tie-breaking: when deficits are equal, prefer alphabetically earlier variant
        if (deficit > maxDeficit || (deficit === maxDeficit && variant < selectedVariant)) {
            maxDeficit = deficit;
            selectedVariant = variant;
        }
    }
    return selectedVariant;
}
/**
 * Compute a deterministic hash bucket for a user
 *
 * @param uid - User ID
 * @param salt - Experiment-specific salt
 * @param stratum - Optional stratum ID for stratified allocation
 * @returns Bucket in range [0, 100000)
 */
function computeExperimentBucket(uid, salt, stratum) {
    const input = stratum ? `${uid}:${salt}:${stratum}` : `${uid}:${salt}`;
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = Math.imul(31, hash) + char;
        hash = hash % 0x100000000;
    }
    // Normalize to 0-100000 range
    return Math.abs(hash % 100000);
}
/**
 * Select a variant based on deterministic hashing
 *
 * @param bucket - Hash bucket in range [0, 100000)
 * @param weights - Variant weights (must sum to 100)
 * @returns Selected variant
 * @throws Error if weights is empty or weights don't sum to 100
 */
function selectVariantByBucket(bucket, weights) {
    // Validate that we have at least one variant
    const variants = Object.keys(weights);
    if (variants.length === 0) {
        throw new Error("Cannot select variant: no variants defined in weights");
    }
    // Store last variant for fallback (guaranteed to exist after length check above)
    const lastVariant = variants[variants.length - 1];
    // Validate that weights sum to 100
    let totalWeight = 0;
    for (const weight of Object.values(weights)) {
        totalWeight += weight;
    }
    if (Math.abs(totalWeight - 100) > 0.001) {
        throw new Error(`Variant weights must sum to 100, got ${totalWeight}`);
    }
    // Normalize bucket to 0-100 range
    const normalizedBucket = bucket / 1000;
    let cumulative = 0;
    for (const [variant, weight] of Object.entries(weights)) {
        cumulative += weight;
        if (normalizedBucket < cumulative) {
            return variant;
        }
    }
    // Return last variant - this handles edge case where normalizedBucket === 100
    // due to floating point precision
    return lastVariant;
}
/**
 * Calculate feasibility status for an experiment
 *
 * @param targetWeights - Target distribution
 * @param observedCounts - Current assignment counts
 * @param totalAssigned - Total users assigned
 * @returns Feasibility status
 */
function calculateFeasibility(targetWeights, observedCounts, totalAssigned) {
    const blockers = [];
    let maxAdditionalNeeded = 0;
    for (const [variant, weight] of Object.entries(targetWeights)) {
        const currentCount = observedCounts[variant] ?? 0;
        // Check if variant has assignments but 0% target
        if (weight === 0 && currentCount > 0) {
            blockers.push(`Variant "${variant}" has ${currentCount} assignments but 0% target weight`);
        }
        // Calculate minimum additional exposures needed
        // N >= (c_i / w_i) - T for each variant
        // Note: The weight > 0 check prevents division by zero. Variants with 0% weight
        // are handled above (they're blockers if they have any assignments).
        if (weight > 0) {
            const minNeeded = Math.ceil(currentCount / (weight / 100) - totalAssigned);
            if (minNeeded > maxAdditionalNeeded) {
                maxAdditionalNeeded = minNeeded;
            }
        }
    }
    // Check for variants in observedCounts that aren't in targetWeights
    for (const [variant, count] of Object.entries(observedCounts)) {
        if (!(variant in targetWeights) && count > 0) {
            blockers.push(`Variant "${variant}" has ${count} assignments but is not in target weights`);
        }
    }
    if (blockers.length > 0) {
        return {
            achievable: false,
            blockers,
        };
    }
    return {
        achievable: true,
        minAdditionalExposures: maxAdditionalNeeded > 0 ? maxAdditionalNeeded : undefined,
    };
}
/**
 * Assign a user to an experiment variant
 *
 * @param run - The experiment run
 * @param uid - User ID
 * @param existingAssignment - Existing assignment if any
 * @param parentVariant - Parent variant for stratified allocation
 * @returns The assigned variant (existing or new)
 */
function assignVariant(run, uid, existingAssignment, parentVariant) {
    // If user already has a valid assignment for this run, return it
    if (existingAssignment && existingAssignment.runId === run.runId) {
        return existingAssignment.variant;
    }
    // Get the weights to use (stratified or global)
    let weights = run.targetWeights;
    if (run.stratification && parentVariant) {
        const stratumConfig = run.stratification.byVariant[parentVariant];
        if (stratumConfig) {
            weights = stratumConfig.weights;
        }
    }
    // Assign based on allocation type
    if (run.allocationType === "deficit") {
        return selectVariantByDeficit(weights, run.observedCounts, run.totalAssigned);
    }
    else {
        // Deterministic hashing
        const bucket = computeExperimentBucket(uid, run.runId, parentVariant);
        return selectVariantByBucket(bucket, weights);
    }
}
/**
 * Create a new experiment run
 */
function createExperimentRun(experimentKey, environment, config) {
    const runId = `${experimentKey}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    return {
        runId,
        experimentKey,
        environment,
        variants: config.variants,
        targetWeights: config.targetWeights,
        eligibility: config.eligibility,
        exposureTrigger: config.exposureTrigger,
        allocationType: config.allocationType ?? "deterministic",
        stratification: config.stratification,
        observedCounts: {},
        totalAssigned: 0,
        status: "draft",
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: config.createdBy,
    };
}
//# sourceMappingURL=experiments.js.map